import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { MoreHorizontal } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getJSON, patchJSON, postJSON, deleteJSON } from '@/lib/api'

type Gift = {
  id: number
  amount: string
  frequency: 'monthly' | 'annual'
  next_charge_on: string
  status: 'active' | 'ended'
  stopped_by: string | null
  campaign: string
  card: { brand: string; last4: string }
}

type Campaign = { id: number; name: string }

export function PersonGivingTab({ contactId }: { contactId: number }) {
  const queryClient = useQueryClient()
  const gifts = useQuery<{ recurring_gifts: Gift[] }>({
    queryKey: ['person-recurring-gifts', contactId],
    queryFn: () => getJSON(`/api/v1/persons/${contactId}/recurring_gifts`),
  })
  const campaigns = useQuery<{ campaigns: Campaign[] }>({
    queryKey: ['campaigns'],
    queryFn: () => getJSON('/api/v1/campaigns'),
  })
  const [amount, setAmount] = React.useState('')
  const [frequency, setFrequency] = React.useState<Gift['frequency']>('monthly')
  const [campaignId, setCampaignId] = React.useState('')
  const createGift = useMutation({
    mutationFn: () =>
      postJSON(`/api/v1/persons/${contactId}/recurring_gifts`, {
        recurring_gift: { amount, frequency, campaign_id: Number(campaignId) },
      }),
    onSuccess: () => {
      setAmount('')
      void queryClient.invalidateQueries({ queryKey: ['person-recurring-gifts', contactId] })
    },
  })

  if (gifts.isLoading || campaigns.isLoading) return <EmptyState title="Loading giving" />
  if (gifts.isError || campaigns.isError) return <EmptyState title="Giving unavailable" />

  const active = gifts.data?.recurring_gifts.filter((gift) => gift.status === 'active') ?? []
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="p-4 pb-3">
          <CardTitle className="text-body font-medium">Recurring gifts</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 p-4 pt-0">
          {active.length === 0 && (
            <p className="text-body-sm text-muted-foreground">No active Recurring Gift.</p>
          )}
          {active.map((gift) => (
            <GiftRow
              key={gift.id}
              gift={gift}
              contactId={contactId}
              campaigns={campaigns.data?.campaigns ?? []}
            />
          ))}
          <div className="grid max-w-2xl gap-2 border-t border-border pt-3 sm:grid-cols-[1fr_auto_1fr_auto]">
            <Input
              aria-label="Amount"
              placeholder="50.00"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
            <div className="flex gap-1" role="group" aria-label="Frequency">
              {(['monthly', 'annual'] as const).map((option) => (
                <Button
                  key={option}
                  type="button"
                  size="sm"
                  variant={frequency === option ? 'default' : 'outline'}
                  onClick={() => setFrequency(option)}
                >
                  {option}
                </Button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1" role="group" aria-label="Campaign">
              {campaigns.data?.campaigns.map((campaign) => (
                <Button
                  key={campaign.id}
                  type="button"
                  size="sm"
                  variant={campaignId === String(campaign.id) ? 'default' : 'outline'}
                  onClick={() => setCampaignId(String(campaign.id))}
                >
                  {campaign.name}
                </Button>
              ))}
            </div>
            <Button
              type="button"
              disabled={!amount || !campaignId || createGift.isPending}
              onClick={() => createGift.mutate()}
            >
              Create
            </Button>
          </div>
          {createGift.isError && (
            <p role="alert" className="text-body-sm text-destructive">
              Could not create the Recurring Gift.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function GiftRow({
  gift,
  contactId,
  campaigns,
}: {
  gift: Gift
  contactId: number
  campaigns: Campaign[]
}) {
  const queryClient = useQueryClient()
  const stop = useMutation({
    mutationFn: () => deleteJSON(`/api/v1/persons/${contactId}/recurring_gifts/${gift.id}`),
    onSuccess: () => {
      setEnding(false)
      void queryClient.invalidateQueries({ queryKey: ['person-recurring-gifts', contactId] })
    },
  })
  const [editing, setEditing] = React.useState(false)
  const [ending, setEnding] = React.useState(false)
  const [amount, setAmount] = React.useState(gift.amount.replace(/[^0-9.-]/g, ''))
  const [frequency, setFrequency] = React.useState(gift.frequency)
  const [campaignId, setCampaignId] = React.useState(
    campaigns.find((campaign) => campaign.name === gift.campaign)?.id.toString() ?? '',
  )
  const [nextChargeOn, setNextChargeOn] = React.useState(gift.next_charge_on)
  const update = useMutation({
    mutationFn: () =>
      patchJSON(`/api/v1/persons/${contactId}/recurring_gifts/${gift.id}`, {
        recurring_gift: {
          amount,
          frequency,
          next_charge_on: nextChargeOn,
          campaign_id: Number(campaignId),
        },
      }),
    onSuccess: () => {
      setEditing(false)
      void queryClient.invalidateQueries({ queryKey: ['person-recurring-gifts', contactId] })
    },
  })
  return (
    <>
      <div className="flex items-center justify-between gap-3 border-b border-border pb-3">
        <div className="min-w-0 text-body-sm">
          <p>{`${gift.amount} · ${gift.frequency}`}</p>
          <p className="text-caption text-muted-foreground">
            Next charge {gift.next_charge_on} · {gift.card.brand} ·· {gift.card.last4} ·{' '}
            {gift.campaign}
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 w-9 px-0"
              aria-label="Recurring gift actions"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => setEditing(true)}>
              Edit recurring gift
            </DropdownMenuItem>
            <DropdownMenuItem destructive onSelect={() => setEnding(true)}>
              End recurring gift
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {stop.isError && <Badge variant="warning">Could not end</Badge>}
      </div>
      <EditGiftDialog
        open={editing}
        onOpenChange={setEditing}
        amount={amount}
        onAmountChange={setAmount}
        frequency={frequency}
        onFrequencyChange={setFrequency}
        campaignId={campaignId}
        onCampaignChange={setCampaignId}
        nextChargeOn={nextChargeOn}
        onNextChargeChange={setNextChargeOn}
        campaigns={campaigns}
        pending={update.isPending}
        error={update.isError}
        onSave={() => update.mutate()}
      />
      <EndGiftDialog
        open={ending}
        onOpenChange={setEnding}
        pending={stop.isPending}
        error={stop.isError}
        onConfirm={() => stop.mutate()}
      />
    </>
  )
}

function EditGiftDialog({
  open,
  onOpenChange,
  amount,
  onAmountChange,
  frequency,
  onFrequencyChange,
  campaignId,
  onCampaignChange,
  nextChargeOn,
  onNextChargeChange,
  campaigns,
  pending,
  error,
  onSave,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  amount: string
  onAmountChange: (value: string) => void
  frequency: Gift['frequency']
  onFrequencyChange: (value: Gift['frequency']) => void
  campaignId: string
  onCampaignChange: (value: string) => void
  nextChargeOn: string
  onNextChargeChange: (value: string) => void
  campaigns: Campaign[]
  pending: boolean
  error: boolean
  onSave: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Recurring Gift</DialogTitle>
          <DialogDescription>Update the donor-editable facts for this schedule.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <Input
            aria-label="Amount"
            value={amount}
            onChange={(event) => onAmountChange(event.target.value)}
          />
          <Select
            value={frequency}
            onValueChange={(value) => onFrequencyChange(value as Gift['frequency'])}
          >
            <SelectTrigger aria-label="Frequency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="annual">Annual</SelectItem>
            </SelectContent>
          </Select>
          <Select value={campaignId} onValueChange={onCampaignChange}>
            <SelectTrigger aria-label="Campaign">
              <SelectValue placeholder="Campaign" />
            </SelectTrigger>
            <SelectContent>
              {campaigns.map((campaign) => (
                <SelectItem key={campaign.id} value={campaign.id.toString()}>
                  {campaign.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            aria-label="Next charge date"
            type="date"
            value={nextChargeOn}
            onChange={(event) => onNextChargeChange(event.target.value)}
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={pending} onClick={onSave}>
            Save changes
          </Button>
        </DialogFooter>
        {error && (
          <p role="alert" className="text-body-sm text-destructive">
            Could not update the Recurring Gift.
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}

function EndGiftDialog({
  open,
  onOpenChange,
  pending,
  error,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  pending: boolean
  error: boolean
  onConfirm: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>End Recurring Gift?</DialogTitle>
          <DialogDescription>
            No further charges will be scheduled. Giving history stays available.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Keep giving
          </Button>
          <Button type="button" variant="destructive" disabled={pending} onClick={onConfirm}>
            End gift
          </Button>
        </DialogFooter>
        {error && (
          <p role="alert" className="text-body-sm text-destructive">
            Could not end the Recurring Gift.
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}
